// The English catalogue (D-013: the only UI language). Every user-facing string, aria-label and
// live-region message in admin, console and the design system comes from here. Copy rules:
// DESIGN_SYSTEM §9.6 (plain verbs, sentence case, errors say what failed and how to fix it, no
// apologies). Placeholders use {name}; plural entries are { one, other } with {count}.

import { catalogStrings } from "./en/catalog";
import { commerceStrings } from "./en/commerce";
import { operationsStrings } from "./en/operations";
import { paymentsStrings } from "./en/payments";
import { fulfilmentStrings } from "./en/fulfilment";
import { integrationsStrings } from "./en/integrations";
import { importsStrings } from "./en/imports";
import { walletStrings } from "./en/wallet";
import { adyWalletStrings } from "./en/adyWallet";
import { plansStrings } from "./en/plans";
import { deskStrings } from "./en/desk";
import { billingOpsStrings } from "./en/billingOps";
import { platformOpsStrings } from "./en/platformOps";
import { trustOpsStrings } from "./en/trustOps";
import { notificationsStrings } from "./en/notifications";
import { analyticsStrings } from "./en/analytics";
import { engagementStrings } from "./en/engagement";
import { automationStrings } from "./en/automation";
import { marketingStrings } from "./en/marketing";
import { headlessStrings } from "./en/headless";
import { storefrontStrings } from "./en/storefront";
import { designsStrings } from "./en/designs";
import { settingsStrings } from "./en/settings";
import { importMarketStrings } from "./en/importMarket";
import { smsServiceStrings } from "./en/smsService";
import { digitalMarketingStrings } from "./en/digitalMarketing";
import { adsStrings } from "./en/ads";
import { payMethodsStrings } from "./en/payMethods";
import { coursesStrings } from "./en/courses";
import { conversationsStrings } from "./en/conversations";
import { darazStrings } from "./en/daraz";
import { aiMascotStrings } from "./en/aiMascot";

export const en = {
  brand: {
    name: "Adyvance",
    owner: "Adyvance owner console",
  },

  common: {
    // Example values shown in empty inputs (formats to copy, not prose).
    samples: {
      phoneNp: "98XXXXXXXX",
      email: "name@example.com",
      urlPrefix: "https://",
      hexColour: "#C0392B",
      codRows: "PTH-10021, 2450, 100, 0\nPTH-10022, 1800, 100, 0",
      codRef: "PTH-10021",
      bankRows: "0007ZXK, 1130, 25\n0007ZXM, 2450, 50",
      bankRef: "0007ZXK",
      indexNowKey: "0123456789ABCDEF0123456789ABCDEF",
      pagePath: "/products",
      walletPath: "/wallet",
      registryId: "PAYMENT-001",
      registrar: "register.com.np",
    },
    // Units and short labels used beside numbers and files.
    units: {
      rs: "Rs",
      percent: "%",
      gb: "GB",
      pdf: "PDF",
      qr: "QR",
      withUnit: "{label} ({unit})",
      qtyTimes: "{qty}x",
      sku: "SKU: {sku}",
      months: "{months} mo",
      used: "{amount} used",
      on: "On",
      off: "Off",
      unlimitedHint: "-1 = unlimited",
      unlimitedHintGb: "GB · -1 = unlimited",
    },
    cancel: "Cancel",
    close: "Close",
    back: "Back",
    retry: "Retry",
    continue: "Continue",
    save: "Save",
    saving: "Saving…",
    edit: "Edit",
    done: "Done",
    more: "More",
    search: "Search",
    optional: "Optional",
    required: "Required",
    loading: "Loading",
    inProgress: "In progress",
    signOut: "Sign out",
    learnMore: "Learn more",
    requestId: "Request ID {id}",
    copy: "Copy",
    copied: "Copied",
    skipToContent: "Skip to main content",
    showDetails: "Show details",
    dismiss: "Dismiss",
    menu: "Menu",
    new: "New",
  },

  states: {
    loadingRegion: "Loading content",
    emptyTitle: "Nothing here yet",
    errorTitle: "Couldn't load this",
    errorBody: "Check your connection and retry.",
    crashTitle: "This part of the page stopped working",
    crashBody: "Your data is safe. Try again, or open another page and come back.",
    updatedTitle: "A new version of Adyvance is ready",
    updatedBody: "Reload to keep going. Nothing you saved is lost.",
    successTitle: "Done",
    permissionDeniedTitle: "You don't have access to this",
    permissionDeniedBody: "Only {who} can open this. Ask them for access.",
    permissionDeniedAction: "Request access",
    permissionWhoOwner: "the store owner",
    planLockedTitle: "{feature} is part of {plan}",
    planLockedBody: "You're on {currentPlan}. Your current usage stays as it is.",
    planLockedOwnerAction: "See plans",
    planLockedStaffAction: "Ask the owner",
    pendingActivationTitle: "Waiting for activation",
    pendingActivationBody:
      "This turns on once {who} finishes the steps below. We'll show it as live only after it's verified.",
    pendingWhoVendor: "you",
    pendingWhoPlatform: "Adyvance",
    pendingWhoThirdParty: "the provider",
    disabledReason: "Not available: {reason}",
    hiddenForRole: "Hidden for your role",
    offline: "You're offline. Changes will need a connection.",
    testMode: "Test mode",
    notSetUpTitle: "Not set up yet",
    comingInPhase: "This area arrives in a later release. Nothing here is live yet.",
  },

  field: {
    errorPrefix: "Error:",
    characters: "{count} of {max} characters",
    fixErrors: { one: "Fix {count} field to continue.", other: "Fix {count} fields to continue." },
  },

  phone: {
    label: "Mobile number",
    prefix: "+977",
    prefixLabel: "Nepal country code, plus 977",
    errors: {
      empty: "Enter a mobile number.",
      invalid_characters: "Use digits only.",
      length: "Enter 10 digits, starting with 9.",
      prefix: "Enter a Nepal mobile number starting with 96, 97 or 98.",
    },
  },

  money: {
    prefix: "Rs",
    prefixLabel: "Nepali rupees",
    inWords: "In words: {words}",
    range: "Between {min} and {max}",
    max: "Up to {max}",
    refundCap: "Up to {max} can be refunded",
    errors: {
      empty: "Enter an amount.",
      invalid: "Enter an amount in numbers, like 2,400.",
      too_many_decimals: "Use at most 2 digits after the decimal point.",
      negative: "Enter an amount of zero or more.",
      too_large: "That amount is too large.",
      below_min: "Enter at least {min}.",
      above_max: "Enter at most {max}.",
    },
  },

  select: {
    placeholder: "Choose one",
  },

  dialog: {
    closeLabel: "Close dialog",
    typeToConfirm: "Type {phrase} to confirm",
    typeMismatch: "Type {phrase} exactly to continue.",
    unsavedTitle: "Discard unsaved changes?",
    unsavedBody: "Your changes on this screen haven't been saved.",
    unsavedDiscard: "Discard changes",
    unsavedKeep: "Keep editing",
  },

  drawer: {
    closeLabel: "Close panel",
    handleLabel: "Close sheet",
  },

  toast: {
    regionLabel: "Notifications",
    undo: "Undo",
    dismissLabel: "Dismiss notification",
  },

  table: {
    loadingRows: "Loading rows",
    rowError: "This row didn't load.",
    selectRow: "Select row",
    selectAll: "Select all rows",
    selectedCount: { one: "{count} selected", other: "{count} selected" },
    clearFilters: "Clear filters",
    noResultsTitle: "No results",
    noResultsBody: "Nothing matches these filters.",
  },

  tooltip: {
    moreInfo: "More information",
  },

  trail: {
    label: "Order progress: {current}. Next: {next}.",
    labelDone: "Order progress: {current}.",
    steps: {
      placed: "Placed",
      confirmed: "Confirmed",
      packed: "Packed",
      with_courier: "With courier",
      delivered: "Delivered",
      settled: "Settled",
    },
    progress: "{done} of {total}",
  },

  status: {
    order: {
      unfinished: "Unfinished checkout",
      new: "New, to confirm",
      confirmed: "Confirmed",
      packed: "Packed, ready to ship",
      with_courier: "With courier",
      delivered: "Delivered",
      cancelled: "Cancelled",
      return_requested: "Return requested",
      returned: "Returned to store",
    },
    payment: {
      unpaid: "Unpaid",
      cod_to_collect: "COD, to collect",
      awaiting_verification: "Awaiting verification",
      processing: "Processing",
      partially_paid: "Partially paid",
      paid: "Paid",
      settled: "Settled",
      failed: "Failed",
      refunded: "Refunded",
    },
    courier: {
      not_booked: "Not booked",
      booked: "Booked",
      picked_up: "Picked up",
      in_transit: "In transit",
      out_for_delivery: "Out for delivery",
      delivered: "Delivered",
      attempt_failed: "Delivery attempt failed",
      returning: "Returning",
      returned: "Returned",
    },
    vendor: {
      invited: "Invited",
      trial: "Trial",
      active: "Active",
      past_due: "Past due",
      suspended: "Suspended",
      offboarding: "Offboarding",
      closed: "Closed",
    },
    job: {
      queued: "Queued",
      running: "Running",
      retrying: "Retrying",
      succeeded: "Succeeded",
      failed: "Failed",
      dead_lettered: "Dead-lettered",
      cancelled: "Cancelled",
    },
    activation: {
      live: "Live",
      pending: "Pending activation",
      test: "Test mode",
    },
  },

  ai: {
    youAsked: "You asked: {request}",
    willChange: "What will change",
    excluded: "Left out",
    sideEffects: "Also happens",
    scope: "Acting as {name} ({role}). Uses your permissions.",
    andMore: { one: "and {count} more", other: "and {count} more" },
    editRequest: "Edit request",
    serverDiffers: "The server's check differs from the preview. The server's result is shown.",
    previewing: "Preparing a preview…",
    readOnly: "Read only",
    viaAssistant: "via root",
    itemToggle: "Include {item}",
  },

  auth: {
    signInTitle: "Log in to your store",
    signInLead: "Use your Google account to continue.",
    localOnly: "Local testing only",
    localOnlyHelp: "Shown on localhost and with mock data, never on a live site.",
    emailLabel: "Email address",
    emailHelp: "We never share it.",
    emailInvalid: "Enter an email address like name@example.com.",
    sendCode: "Send code",
    sendingCode: "Sending code…",
    or: "or",
    google: "Continue with Google",
    passkey: "Log in with a passkey",
    passkeyUnsupported: "This browser doesn't support passkeys. Use the email code instead.",
    passkeyCancelled: "Passkey log-in was cancelled. Try again or use the email code.",
    googleNeedsServer: "Google log-in needs the API server. It isn't available with mock data.",
    googleFailed: "Google log-in didn't finish. Try again.",
    phonePending: "Log-in by phone arrives once SMS is activated.",
    verifyTitle: "Check your email",
    verifyLead: "Enter the 6-digit code we sent to {email}. It expires in 10 minutes.",
    codeLabel: "6-digit code",
    codeInvalid: "Enter the 6 digits from the email.",
    verify: "Verify and log in",
    verifying: "Verifying…",
    resendIn: "Resend code in {seconds} s",
    resend: "Resend code",
    resent: "We sent a new code.",
    changeEmail: "Use a different email",
    signedIn: "Logged in",
    signedOut: "Logged out",
    needsOrgTitle: "Set up your business",
    needsOrgBody: "Your account has no business yet. Set one up to open your store.",
    errors: {
      otp_invalid: "That code isn't right. Check the email and try again.",
      otp_expired: "That code has expired. Send a new one.",
      otp_locked: "Too many wrong codes. Wait a few minutes, then send a new code.",
      mfa_required: "This account needs a second step to log in.",
      stepup_required: "Confirm it's you to continue.",
      session_revoked: "You were logged out. Log in again.",
      unauthenticated: "Log in to continue.",
      rate_limited: "Too many attempts. Wait {seconds} seconds and try again.",
      generic: "Log-in didn't work. Try again.",
    },
    mfa: {
      title: "Confirm it's you",
      lead: "Your account uses two-step log-in. Enter the code from your authenticator app, or use a passkey.",
      totpLabel: "Authenticator code",
      verify: "Confirm",
      usePasskey: "Use a passkey instead",
    },
  },

  dev: {
    mocksFailedTitle: "The mock API couldn't start",
    mocksFailedBody:
      "Service workers may be blocked in this browser. Allow them and reload, or run with VITE_ADY_MOCKS=off against a local API.",
  },

  legal: {
    title: "Review and accept",
    lead: "Adyvance has published documents you need to accept before you continue.",
    kinds: { terms: "Terms of service", privacy: "Privacy notice" },
    version: "Version {version}",
    open: "Read {name} (opens in a new tab)",
    accept: "Accept and continue",
    accepting: "Saving…",
  },

  onboarding: {
    title: "Set up your business",
    lead: "Name your business and choose your store link. You can change the store name later.",
    resumeTitle: "Finish setting up {name}",
    resumeLead: "Your business is ready. Choose a link to open your store.",
    orgNameLabel: "Business name",
    orgNameHelp: "As customers know it. You can write it in Nepali or English.",
    orgNameRequired: "Enter your business name.",
    slugLabel: "Store link",
    slugHelp: "Lowercase letters, numbers and hyphens. Customers will visit {host}.",
    slugHelpEmpty: "Lowercase letters, numbers and hyphens, for example himal-threads.",
    slugRequired: "Choose a store link.",
    slugFormat: "Use 3 to 63 lowercase letters, numbers or single hyphens.",
    slugTaken: "This link is taken. Try another one.",
    slugReserved: "This link is reserved. Try another one.",
    submit: "Create my store",
    submitting: "Creating your store…",
    resumeSubmit: "Open my store",
    nextSteps: "Next you'll add products, delivery areas and payment methods from Home.",
    storeLimit: "Your plan allows one store, and it's already set up.",
  },

  security: {
    // D-126: the account's mobile number for "Confirm it's you" by text.
    phone: {
      title: "Mobile number",
      body: "Add your mobile number to confirm it's you with a text message. Each code uses 1 SMS credit from your shop.",
      strongNote:
        "You confirm with an authenticator app or passkey, so text codes aren't offered to you. You can still add a number.",
      label: "Mobile number",
      placeholder: "98XXXXXXXX",
      help: "A Nepal number like 98XXXXXXXX. We text it a code to make sure it's yours.",
      payer: "Paid from",
      send: "Send code",
      codeHelp: "Enter the code we texted to {phone}.",
      confirm: "Confirm number",
      change: "Change number",
      verified: "Verified",
      remove: "Remove",
      added: "Mobile number added",
      removed: "Mobile number removed",
      invalid: "Enter a mobile number like 98XXXXXXXX.",
      taken: "This number is on another Adyvance account.",
      noCredits: "This shop has no SMS credits left. Buy credits in SMS, or choose another shop.",
      noShop: "Join or create a shop first: its SMS credits pay for the code.",
      inactive: "Text messages aren't available yet.",
    },
    title: "Security and sessions",
    lead: "Manage how you sign in and where you're signed in.",
    passkeysTitle: "Passkeys",
    passkeysBody: "Sign in with your fingerprint, face or device PIN instead of an email code.",
    addPasskey: "Add a passkey",
    passkeyAdded: "Passkey added",
    noPasskeys: "No passkeys yet.",
    totpTitle: "Authenticator app",
    totpBody:
      "Adds a second step at sign-in. Required for owners and anyone with sensitive permissions.",
    totpEnabled: "On",
    totpOff: "Off",
    totpStart: "Set up authenticator",
    totpScanTitle: "Add Adyvance to your authenticator app",
    totpScanBody:
      "Open your authenticator app and add an account with this setup key. It's shown only once.",
    totpSecretLabel: "Setup key",
    totpUriLabel: "Setup link",
    totpConfirmLabel: "Code from the app",
    totpConfirm: "Turn on authenticator",
    totpConfirmed: "Authenticator turned on",
    sessionsTitle: "Where you're signed in",
    sessionsBody: "Signing out a device takes effect immediately.",
    thisDevice: "This device",
    lastActive: "Last active {when}",
    signedInAt: "Signed in {when}",
    revoke: "Sign out",
    revokeAll: "Sign out all other devices",
    revokeConfirmTitle: "Sign out {device}?",
    revokeConfirmBody: "The device will need to sign in again.",
    revoked: "Signed out {device}",
    revokedAll: "Signed out all other devices",
    noOtherSessions: "You're only signed in on this device.",
    columns: {
      device: "Device",
      location: "Location",
      active: "Last active",
      actions: "Actions",
    },
    unknownDevice: "Unknown device",
    sessionsLead: "Every device that is signed in to your account.",
    sessionsListLabel: "Signed-in devices",
    recoveryTitle: "Save your recovery codes",
    recoveryBody:
      "Each code works once if you lose your authenticator app or passkey. They're shown only now, so store them somewhere safe.",
    recoveryCopy: "Copy codes",
    recoveryCopied: "Recovery codes copied",
    recoveryDone: "I've saved them",
    passkeyListLabel: "Your passkeys",
    passkeyName: "Passkey {n}",
    passkeyAddedOn: "Added {when}",
    passkeyLastUsed: "Last used {when}",
    passkeyNeverUsed: "Not used yet",
    passkeysLoadFailed: "Couldn't load your passkeys. Retry.",
  },

  stepUp: {
    title: "Confirm it's you",
    lead: "This change needs a fresh check. Choose how to confirm.",
    methodsLabel: "How do you want to confirm?",
    methods: {
      otp: "Email me a code",
      totp: "Authenticator app",
      passkey: "Passkey",
      recovery_code: "Recovery code",
      sms: "Text me a code",
    },
    smsSent: "We sent a 6-digit code to {phone}. It uses 1 SMS credit from {shop}.",
    otpSending: "Sending a code…",
    otpSent: "We sent a 6-digit code to {email}.",
    codeLabel: "6-digit code",
    codeInvalid: "Enter the 6 digits.",
    recoveryLabel: "Recovery code",
    recoveryInvalid: "Enter one of your recovery codes.",
    passkeyHint: "Your browser will ask for your fingerprint, face or device PIN.",
    chooseFirst: "Choose a way to confirm first.",
    confirm: "Confirm",
    confirming: "Confirming…",
    noMethods: "This account has no way to confirm right now. Sign out, then sign in again.",
    cancelled: "Nothing changed. Confirming it's you is needed for this.",
  },

  team: {
    title: "Team and roles",
    lead: "Invite people to help run {org} and choose what each person can do.",
    membersTitle: "Members",
    membersListLabel: "Team members",
    invitationsTitle: "Pending invitations",
    invitationsListLabel: "Pending invitations",
    invite: "Invite someone",
    you: "You",
    primaryOwner: "Business owner",
    suspended: "Suspended",
    nameMissing: "Name not added yet",
    joined: "Joined {when}",
    roleFor: "Role for {name}",
    saveRole: "Save role",
    savingRole: "Saving…",
    roleUnchanged: "Choose a different role first.",
    roleSaved: "{name} is now {role}",
    remove: "Remove",
    removeTitle: "Remove {name}?",
    removeBody:
      "They lose access to {org} right away. Their past activity stays in the activity log.",
    removed: "{name} was removed",
    seats: "{used} of {limit} team seats used, counting pending invitations",
    seatsUnlimited: "{used} team seats used",
    onlyYouTitle: "Only you so far",
    onlyYouBody:
      "Invite people to share the work. Each person gets only the access their role allows.",
    invitationsEmptyTitle: "No pending invitations",
    invitationsEmptyBody:
      "Invitations show here until they're accepted, revoked or expire. Each link works once.",
    invitationExpires: "Expires {when}",
    allStores: "All stores",
    someStores: { one: "{count} store", other: "{count} stores" },
    revoke: "Revoke",
    revokeTitle: "Revoke the invitation for {target}?",
    revokeBody: "The link stops working right away. You can invite them again later.",
    revoked: "Invitation revoked",
    unknownRole: "Role no longer exists",
    rolesNeedOwner: "Only the owner can choose roles for now.",
    rolesLoadFailed: "Couldn't load the roles. Close this and retry.",
    noRoleChange: "Role changes need the owner.",
    // Compact Team page (owner, 2026-11-04).
    summary: {
      members: { one: "{count} member", other: "{count} members" },
      pending: { one: "{count} pending invitation", other: "{count} pending invitations" },
    },
    rolesTab: "Roles",
    cols: {
      person: "Person",
      role: "Role",
      joined: "Joined",
      actions: "Actions",
      email: "Email",
      stores: "Stores",
      expires: "Expires",
      type: "Type",
      access: "Access",
      people: "People",
    },
    saveShort: "Save",
    newRole: "New role",
    roleCustom: "Custom",
    rolePreset: "Preset",
    permCount: { one: "{count} permission", other: "{count} permissions" },
    inviteDialog: {
      title: "Invite someone",
      lead: "They get the role you choose. You can change it later.",
      emailLabel: "Email address",
      emailHelp: "They must sign in with this email to accept.",
      emailInvalid: "Enter an email address like name@example.com.",
      alreadyInvited: "This person already has a pending invitation.",
      roleLabel: "Role",
      rolePlaceholder: "Choose a role",
      roleRequired: "Choose a role.",
      storesLegend: "Store access",
      storesHelp: "Leave every store unticked to give access to all stores.",
      submit: "Create invitation",
      submitting: "Creating invitation…",
      escalation: "You can't give someone more access than your own role has.",
      createdTitle: "Invitation ready to share",
      createdBody:
        "Email delivery isn't active yet, so send this link to {target} yourself, for example in a message.",
      linkLabel: "Invitation link",
      linkHelp: "It works once, only for {target}, and expires {when}.",
      copy: "Copy link",
      copied: "Invitation link copied",
      copyFailed: "Couldn't copy. Select the link and copy it.",
      inviteAnother: "Invite someone else",
      done: "Done",
      customButton: "Custom permissions",
      customHint: "Pick exactly what they can do, and save it as a role.",
      sentTitle: "Invitation sent",
      sentBody:
        "We emailed an invitation to {target}. They accept it by signing in with that email. If it doesn't arrive, send them the link below.",
    },
    // Custom permissions (owner, 2026-11-04): saved as a named role, chosen area by area.
    customRole: {
      title: "Custom permissions",
      lead: "Choose what this person can do. It's saved as a role you can give to others and change later.",
      editTitle: "Edit permissions",
      editLead: "Changes apply to everyone with the role {name}, including pending invitations.",
      editButton: "Edit permissions",
      editShort: "Permissions",
      changed: "This role changed meanwhile. Close this, then open it again.",
      updated: "Permissions for \u201c{name}\u201d saved",
      nameLabel: "Role name",
      namePlaceholder: "e.g. Packer, Shop helper",
      nameRequired: "Give the role a name.",
      nameTaken: "A role with this name already exists. Choose another name.",
      noneChosen: "Turn on at least one permission.",
      escalation: "You can't give more access than your own role has.",
      view: "View",
      manage: "Manage",
      advanced: "Advanced",
      hideAdvanced: "Hide",
      sensitive: "Sensitive",
      sensitiveHelp:
        "Only under Advanced: money, exports, deleting and keys. Give these with care.",
      chosen: { one: "{count} permission chosen", other: "{count} permissions chosen" },
      save: "Save role",
      saving: "Saving role…",
      cancel: "Cancel",
      saved: "Role “{name}” saved",
      loadFailed: "Couldn't load the permissions. Close this and try again.",
      areas: {
        orders: "Orders",
        catalog: "Products",
        inventory: "Inventory",
        customers: "Customers",
        pos: "Counter (POS)",
        shipping: "Delivery",
        payments: "Payments",
        finance: "Money and accounts",
        reports: "Reports",
        marketing: "Marketing",
        content: "Content and Sampark",
        theme: "Online store design",
        media: "Photos and files",
        store: "Store settings",
        settings: "Checkout and tax settings",
        risk: "Fraud and COD checks",
        integrations: "Apps and integrations",
        ai: "root AI",
        team: "Team",
        billing: "Plan and wallet",
        audit: "Activity log",
        org: "Business details",
      },
      areaHelp: {
        orders: "See orders; create, edit, cancel and handle returns.",
        catalog: "See products; add, edit, import and delete them.",
        inventory: "See stock; adjust and move it between locations.",
        customers: "See customers; edit them and see phone and address.",
        pos: "Sell at the counter, give discounts and run shifts.",
        shipping: "Book couriers and change delivery settings.",
        payments: "See payments and confirm manual ones.",
        finance: "See money; post entries, reconcile, expenses and suppliers.",
        reports: "See sales reports and profit.",
        marketing: "Coupons, campaigns, automations, loyalty and reviews.",
        content: "Pages, blog and the Sampark inbox.",
        theme: "Edit and publish the shop's look.",
        media: "See and upload photos and files.",
        store: "See and change store settings, policies and domains.",
        settings: "Tax, checkout and notification settings.",
        risk: "Fraud rules and COD confirmation calls.",
        integrations: "Connect apps and integrations.",
        ai: "Use root and change its settings.",
        team: "See the team and invite or remove people.",
        billing: "See the plan, invoices and wallet.",
        audit: "See who did what.",
        org: "See and change the business details.",
      },
      perms: {
        org_read: "See business details",
        org_update: "Change business details",
        org_kyc_manage: "Business verification",
        team_read: "See the team",
        team_invite: "Invite people",
        team_remove: "Remove people",
        roles_manage: "Create and change roles",
        store_read: "See store settings",
        store_settings_update: "Change store settings",
        store_legal_update: "Change policies and legal pages",
        store_domains_manage: "Manage domains",
        store_publish: "Open or close the store",
        theme_edit: "Edit the theme",
        theme_publish: "Publish the theme",
        theme_code: "Use the code editor",
        content_edit: "Edit pages and blog",
        content_publish: "Publish pages and blog",
        inbox_read: "Read Sampark chats",
        inbox_manage: "Reply in Sampark",
        products_read: "See products",
        products_write: "Add and edit products",
        products_bulk: "Import and bulk edit",
        products_delete: "Delete products",
        products_cost_read: "See cost prices",
        digital_keys_decrypt: "See digital product keys",
        media_read: "See photos and files",
        media_write: "Upload photos and files",
        inventory_read: "See stock",
        inventory_adjust: "Change stock",
        inventory_transfer: "Move stock",
        locations_manage: "Manage locations",
        customers_read: "See customers",
        customers_pii_read: "See phone numbers and addresses",
        customers_write: "Edit customers",
        customers_ban: "Block customers",
        customers_export: "Export customers",
        customers_delete: "Delete customers",
        orders_read: "See orders",
        orders_create: "Create orders",
        orders_update: "Update order status",
        orders_cancel: "Cancel orders",
        orders_edit_items: "Change order items",
        orders_delete: "Delete orders",
        orders_export: "Export orders",
        returns_manage: "Handle returns",
        invoices_issue: "Issue invoices",
        payments_read: "See payments",
        payments_verify_manual: "Confirm manual payments",
        payments_override: "Override payment status",
        payments_refund: "Refund payments",
        payments_credentials_manage: "Change payment keys",
        shipping_settings: "Change delivery settings",
        shipping_book: "Book couriers",
        courier_credentials_manage: "Change courier keys",
        pos_sell: "Sell at the counter",
        pos_discount: "Give counter discounts",
        pos_refund: "Counter refunds",
        pos_shift_manage: "Open and close shifts",
        pos_registers_manage: "Manage registers",
        finance_read: "See money and accounts",
        finance_post: "Post entries",
        finance_reconcile: "Reconcile",
        expenses_manage: "Manage expenses",
        suppliers_manage: "Manage suppliers",
        reports_read: "See reports",
        reports_profit_read: "See profit",
        promotions_manage: "Coupons and discounts",
        campaigns_send: "Send campaigns",
        automations_manage: "Automations",
        loyalty_manage: "Loyalty points",
        affiliates_manage: "Affiliates",
        reviews_moderate: "Moderate reviews",
        risk_manage: "Fraud rules",
        cod_verify: "COD confirmation calls",
        settings_tax: "Tax settings",
        settings_checkout: "Checkout settings",
        settings_notifications: "Notification settings",
        integrations_manage: "Integrations",
        apps_manage: "Apps",
        webhooks_manage: "Webhooks",
        api_keys_manage: "API keys",
        ai_use: "Use root",
        ai_settings: "root settings",
        billing_read: "See plan and invoices",
        billing_manage: "Change the plan",
        wallet_spend: "Spend from the wallet",
        audit_read: "See the activity log",
        data_export: "Export all data",
      },
    },
    quota: {
      title: "Your plan's team limit is reached",
      body: "Your {plan} includes {limit} team seats and {used} are in use, counting pending invitations. Remove a member or revoke an invitation to invite someone new.",
      bodyGeneric:
        "Your plan's team seats are all in use, counting pending invitations. Remove a member or revoke an invitation to invite someone new.",
      later: "Paid plans with more seats will be offered later.",
    },
  },

  invitation: {
    title: "Join a team on Adyvance",
    lead: "You were invited to help run a business on Adyvance. Accept to open it.",
    offer: "{inviter} invited you to join {org} as {role}.",
    offerNoInviter: "You're invited to join {org} as {role}.",
    signedInAs: "Signed in as {email}",
    rule: "Only the email address the invitation was sent to can accept it, and the link works once.",
    accept: "Accept invitation",
    accepting: "Joining…",
    switchAccount: "Sign in with another email",
    openAdyvance: "Open Adyvance",
    noStoreTitle: "You joined {org}",
    noStoreBody:
      "This business has no store yet. The owner needs to finish setting it up before you can open it.",
    errors: {
      usedTitle: "This invitation was already used",
      usedBody:
        "Each invitation link works once. If you accepted it, open Adyvance to find the business.",
      expiredTitle: "This invitation has expired",
      expiredBody: "Ask the person who invited you for a new link.",
      notFoundTitle: "This invitation link isn't valid",
      notFoundBody:
        "It may have been revoked, or only part of the link was copied. Ask for a new one.",
      mismatchTitle: "This invitation is for a different email",
      mismatchBody: "Sign in with the email address the invitation was sent to.",
      memberTitle: "You're already in this team",
      memberBody: "Open Adyvance to go to the business.",
    },
  },

  activity: {
    title: "Activity log",
    lead: "Sign-ins, team changes and settings changes in {org}. Entries can't be edited or deleted.",
    listLabel: "Activity, newest first",
    emptyTitle: "No activity yet",
    emptyBody: "Team, sign-in and settings changes will be recorded here.",
    loadMore: "Load older activity",
    loadingMore: "Loading older activity…",
    end: "You've reached the first entry.",
    by: "By {actor}",
    you: "you",
    actorTypes: {
      user: "a team member",
      api_key: "an API key",
      mcp_grant: "a connected app",
      ai_on_behalf: "root, your AI assistant",
      system: "Adyvance",
      platform_staff: "Adyvance support",
      unknown: "someone",
    },
    target: "{type} {id}",
    roleChange: "{from} to {to}",
    actions: {
      auth: {
        signin: "Signed in",
        signup: "Created an account",
        signout: "Signed out",
        session_revoked: "Signed out a device",
        sessions_revoked_others: "Signed out all other devices",
        stepup: "Confirmed their identity",
        mfa_enrolled: "Turned on two-step sign-in",
        recovery_codes_regenerated: "Created new recovery codes",
        account_deleted: "Deleted their account",
      },
      team: {
        member: {
          role_changed: "Changed a member's role",
          removed: "Removed a member",
          left: "Left the business",
        },
        invitation: {
          created: "Invited someone",
          revoked: "Revoked an invitation",
          accepted: "Accepted an invitation",
        },
        role: { created: "Created a role", updated: "Changed a role" },
      },
      member: { role_changed: "Changed a member's role" },
      api_key: { created: "Created an API key", revoked: "Revoked an API key" },
      org: {
        created: "Created the business",
        updated: "Updated the business details",
        closed: "Closed the business",
        ownership_transfer: {
          requested: "Started an ownership transfer",
          accepted: "Accepted an ownership transfer",
          cancelled: "Cancelled an ownership transfer",
        },
      },
      store: { created: "Created a store" },
      legal: { accepted: "Accepted the platform terms" },
    },
  },

  billing: {
    title: "Plan and billing",
    lead: "Your plan, what it includes and how much of it {org} uses.",
    planTitle: "Current plan",
    planStates: {
      active: "Active",
      trial: "Trial",
      grace: "Grace period",
      past_due: "Payment due",
      cancelled: "Cancelled",
      expired: "Ended",
      unknown: "Status unknown",
    },
    since: "Since {when}",
    noPlan: "No plan is recorded for this business yet.",
    freeNote:
      "Plans are paid from your Adyvance Wallet: no card needed. Choose or renew a plan on the Wallet page.",
    usageTitle: "Usage",
    usageLead: "Counts update as you work. Orders from your store are never blocked by a limit.",
    limitsTitle: "Other limits",
    notIncluded: "Not included in this plan",
    unlimited: "Unlimited",
    usedOf: "{used} of {limit}",
    usedOfMonth: "{used} of {limit} this month",
    days: "{limit} days",
    perMinute: "{limit} requests a minute",
    quotas: {
      stores: "Stores",
      products: "Products",
      orders_month: "Orders",
      staff: "Team seats",
      locations: "Locations",
      pos_registers: "POS registers",
      storage_bytes: "Media storage",
      theme_versions: "Saved theme versions",
      audit_retention_days: "Activity log history",
      api_keys: "API keys",
      webhook_endpoints: "Webhook endpoints",
      api_rate_per_min: "API requests",
      automations: "Automations",
      ai_credits_month: "root AI credits",
      sms_month: "SMS messages",
      test_mode_stores: "Test stores",
      unknown: "Other limit",
    },
  },

  meter: {
    unlimited: "Unlimited",
    soft: "Close to the limit",
    atLimit: "At the limit",
    over: "Over the limit",
  },

  shell: {
    mainNav: "Main navigation",
    navSections: { customize: "Customizations", services: "Adyvance Services" },
    settingsSections: "Settings sections",
    posMode: "POS Mode",
    // Top bar (owner, 2026-10-25): opens the store shoppers see, in a new tab.
    visitShop: "Visit your shop",
    visitShopLabel: "Visit your shop ({status}). Opens in a new tab",
    // The account menu behind the avatar (owner, 2026-10-25).
    profileMenu: {
      twoStepOn: "Two-step on",
      twoStepOff: "Turn on two-step",
      support: "Adyvance support view until {time}",
      planTitle: "Your plan and Adyvance Wallet",
      switch: "Switch",
      links: "Your account",
      profile: "Profile and security",
      notifications: "Notification settings",
      team: "Team and roles",
      billing: "Plan and billing",
    },
    // The Help button's dropdown (owner, 2026-10-25).
    helpMenu: {
      title: "How can we help?",
      searchPh: "Search help guides",
      popular: "Popular guides",
      results: "Guides",
      noMatch: "No guide matches. Ask our team instead.",
      askRoot: "Ask root, our AI helper",
      contact: "Write to the Adyvance team",
      openTickets: { one: "{count} open", other: "{count} open" },
      videos: "Watch how-to videos",
      ideas: "Suggest an idea",
      whatsNew: "What's new",
      centre: "Open Help centre",
      searchAll: "Search everything",
    },
    /** D-068: the blue tick once Adyvance approves the store verification. */
    verified: "Verified by Adyvance",
    unverified: "Unverified",
    unverifiedHint:
      "Not verified yet. An Owner can send verification in Settings → Store profile → Business details.",
    storeSwitcher: {
      label: "Current store: {store}, {org}. Switch store",
      title: "Switch store",
      roleAndPlan: "{role} · {plan}",
      current: "Current store",
      dirtyConfirm: "Switch store and discard unsaved changes?",
    },
    search: "Search or jump to…",
    searchShortcut: "Ctrl K",
    sell: "Sell",
    notifications: "Notifications",
    notificationsEmpty: "No notifications yet. Order and payment alerts will show here.",
    help: "Help",
    reportProblem: "Report a problem",
    assistant: "root",
    openAssistant: "Open root, your AI assistant",
    helpPending: "Help articles and problem reports arrive in a later release.",
    account: "Account",
    accountMenu: "Account menu for {name}",
    securityLink: "Security and sessions",
    theme: "Theme",
    themeSystem: "Match device",
    themeLight: "Light",
    themeDark: "Dark",
    moreTitle: "All areas",
    moreSearch: "Find an area",
    collapseNav: "Collapse navigation",
    expandNav: "Expand navigation",
    mockBanner:
      "Mock data: this dev build is not connected to the real server. Your changes stay in this browser.",
    mockReset: "Reset sample data",
    mockSwitch: "Test switch on: product lists answer “{mode}”.",
    mockSwitchOff: "Turn off",
    mockResetConfirm:
      "Start again with the original sample data? Your changes in this browser are cleared.",
    loadingApp: "Loading Adyvance",
    signInRequired: "Sign in to continue.",
  },

  palette: {
    label: "Command palette",
    placeholder: "Type a command or search",
    sections: {
      records: "Records",
      actions: "Actions",
      goTo: "Go to",
      help: "Help",
    },
    noResults: "No matches for \u201C{query}\u201D.",
    needsPermission: "Needs {permission} permission",
    recordsPending: "Record search arrives with orders and products.",
    later: "Arrives in a later release",
    hint: "Arrows to move, Enter to open, Esc to close",
    actions: {
      switchStore: "Switch store",
      openPos: "Open POS",
      createOrder: "Create order",
      addProduct: "Add product",
      reportProblem: "Report a problem",
      toggleTheme: "Change theme",
    },
  },

  nav: {
    groups: {
      today: "Home",
      conversations: "Sampark",
      daraz: "Daraz",
      help: "Help centre",
      importMarketplace: "Product Import",
      adyWallet: "Wallet",
      digitalMarketing: "Digital Marketing",
      designContent: "Design & Content Creation",
      growthStrategy: "Business Growth Strategy",
      smsService: "SMS Service",
      courses: "Courses",
      orders: "Orders",
      sell: "Sell",
      products: "Products",
      customers: "Customers",
      money: "Earnings",
      marketing: "Marketing",
      store: "Online store",
      templates: "Templates",
      reports: "Reports",
      apps: "Apps",
      settings: "Settings",
    },
    items: {
      orders: "All orders",
      unfinished: "Unfinished checkouts",
      dispatch: "Dispatch",
      returns: "Returns and exchanges",
      invoices: "Invoices and receipts",
      codChecks: "COD confirmation calls",
      pos: "POS sell screen",
      sales: "Counter sales",
      registers: "Registers and cash drawers",
      products: "Products",
      categories: "Categories",
      collections: "Collections",
      brands: "Brands",
      inventory: "Inventory",
      transfers: "Stock transfers",
      reviews: "Reviews",
      import: "Import and export",
      media: "Media library",
      featured: "Featured sections",
      digitalProducts: "Digital products",
      customers: "Customers",
      segments: "Segments",
      loyalty: "Loyalty",
      inbox: "Inbox",
      newsletter: "Newsletter subscribers",
      moneyOverview: "Earnings overview",
      payments: "Online payments",
      cod: "COD settlements",
      accounts: "Cash and bank",
      expenses: "Expenses",
      suppliers: "Suppliers and payables",
      statements: "Statements and profit & loss",
      promotions: "Promotions",
      flashDeals: "Flash deals",
      links: "Checkout links",
      automations: "Automations",
      messages: "Messages",
      affiliates: "Affiliates",
      theme: "Theme and editor",
      content: "Pages, blog and forms",
      menus: "Menus",
      domains: "Domains",
      seo: "SEO and product feeds",
      policies: "Policies and legal pages",
      blog: "Blog",
      branches: "Branches",
      sellingPoints: "Selling points",
      mcp: "MCP server",
      apiKeys: "API keys",
      webhooks: "Webhooks",
      partialPayment: "Partial payment",
      offerPopup: "Offer pop-up",
      themeLibrary: "Theme library",
      pageEditor: "Page editor",
      codeEditor: "Code editor",
      reports: "Reports",
      traffic: "Visitors and traffic",
      moveStore: "Move your store",
      integrations: "Integrations",
      developers: "Developer",
      storeProfile: "Store profile",
      locations: "Locations and outlets",
      paymentSettings: "Payments",
      delivery: "Delivery and couriers",
      couriers: "Courier accounts",
      installation: "Installation services",
      checkout: "Checkout and order protection",
      orderProtection: "Order protection",
      taxes: "Taxes and invoices",
      notificationSettings: "Notifications and email",
      region: "Language and region",
      markets: "Markets",
      email: "Store email",
      help: "Help centre",
      team: "Team and roles",
      billing: "Plan and billing",
      activity: "Activity log",
      organization: "Organization",
    },
    bottom: {
      drawer: "Drawer",
      sales: "Sales",
    },
  },

  roles: {
    owner: "Owner",
    manager: "Manager",
    order_manager: "Order staff",
    cashier: "Cashier",
    inventory: "Inventory",
    marketing: "Marketing",
    accountant: "Accountant",
    viewer: "Viewer",
    custom: "Custom role",
  },

  today: {
    title: "Home",
    needsYou: "Needs you",
    needsYouEmptyTitle: "All clear",
    needsYouEmpty: "Orders to confirm and payments to check will line up here.",
    money: "Money",
    moneyEmptyTitle: "No money movements yet",
    moneyEmpty: "Collected, with-courier and due amounts show here once orders arrive.",
    moneyUpdated: "Updated {when}",
    sales: "Sales",
    salesEmptyTitle: "No sales yet",
    salesEmpty: "Share your store link to get the first order.",
    home: {
      dateOnly: "Today is",
      greeting: {
        morning: "Good morning, {name}",
        afternoon: "Good afternoon, {name}",
        evening: "Good evening, {name}",
      },
      statsFor: "Here are your stats for today",
      doWithAi: "Do with root",
      aiPending:
        "Before root, your AI assistant, can act on your store, it needs activation. Nothing runs until it is live.",
      currency: "Rs",
      periodLabel: "{card} period",
      revenue: "Revenue",
      orders: "Orders",
      average: "Average order value",
      subs: {
        revenue: {
          today: "Sales so far today",
          "7d": "Sales in the last 7 days",
          "30d": "Sales in the last 30 days",
        },
        orders: {
          today: "Orders placed today",
          "7d": "Orders placed in the last 7 days",
          "30d": "Orders placed in the last 30 days",
        },
        average: {
          today: "Average basket today",
          "7d": "Average basket, last 7 days",
          "30d": "Average basket, last 30 days",
        },
      },
      trend: "{card} trend",
      quickLinks: "Quick links",
      quick: {
        importMarketplace: "Buy stock from suppliers outside Nepal.",
        digitalMarketing: "Ads, social media and campaigns by our team.",
        daraz: "Orders, stock and payouts from your Daraz shop.",
        courses: "Learn to sell more with short video lessons.",
        money: "Cash, bank, COD and payments at a glance.",
      },
      ranking: {
        title: "Adyvance leaderboard",
        sub: "{month} · updated daily",
        bandHint: "Your net sales band among stores that joined",
        vsNext: "Your sales vs the next band",
        nextBand: "More sales move you into the {band}.",
        topBand: "You are in the top band. Keep it up.",
        yourBands: "Your bands this month",
        privacy: "Only bands are shown. No store's name or sales are shared with other stores.",
        viewAll: "View all",
        join: "Join the leaderboard to see your band.",
      },
      hourly: {
        title: "Hourly order statistics",
        empty: "No orders yet today.",
        axis: "Orders",
        hour: "{hour}:00",
        tooltip: "{hour}:00 · {count} orders",
      },
      weekly: {
        title: "Weekly sales",
        sub: "Sunday to today, against the same days last week",
        thisWeek: "This week",
        lastWeek: "Last week",
        today: "Today",
        noLastWeek: "No sales in the same days last week",
        change: "{sign}{pct}% vs the same days last week",
      },
      queueTitle: "Needs you",
      // STORE-07 launch checklist on Home until every item is done (owner, 2026-10-29).
      setup: {
        title: "Get your shop ready",
        lead: "Finish these so customers can find your shop and order.",
      },
    },
  },

  hubs: {
    leads: {
      store: "Everything that shapes your online store, in one place.",
      apps: "Apps, integrations and developer tools connected to your store.",
      settings: "Every setting for your store and business, in one place.",
    },
    cardFallback: "Open this page.",
    cards: {
      moveStore:
        "Bring products, stock and customers from Shopify, Daraz, Facebook or a spreadsheet.",
      integrations: "Connect marketplaces, pixels, accounting and messaging tools.",
      developers: "API keys, the MCP server for AI apps, and webhooks for your own software.",
      storeProfile: "Store name, contact, social links, status and business details.",
      digitalProducts: "Sell files, licence keys and links, delivered after payment.",
      locations: "Your shops, warehouses and outlets.",
      paymentSettings: "Payment methods and gateway accounts.",
      delivery: "Delivery areas, fees and couriers.",
      couriers: "Your courier company accounts.",
      installation: "Installation and fitting services you offer.",
      checkout: "The checkout form, order protection rules and deposits.",
      orderProtection: "Rules that catch fake and risky orders.",
      partialPayment: "Take a deposit now and the rest on delivery.",
      loyalty: "Points on paid orders, member levels and rewards.",
      taxes: "VAT, tax rates and what your invoices show.",
      notificationSettings: "Your alerts, the messages customers get and your store email.",
      region: "Calendar, Nepal settings and the markets you sell to.",
      markets: "Countries you sell to and their prices.",
      email: "The address and look of emails to customers.",
      help: "Guides, answers and how to reach support.",
      team: "Invite staff and choose what each can do.",
      billing: "Your Adyvance plan, usage, credit and invoices.",
      activity: "A record of who changed what, and when.",
      organization: "Business name, verification for payouts and the activity log.",
      theme: "Live editor: colours, logo, favicon, header, footer and home page.",
      pageEditor: "Design sale, story and landing pages with a live preview.",
      codeEditor: "Edit theme files and add your own CSS, with a live preview.",
      content:
        "Your pages and their live preview editor, blog posts, About, FAQ and contact forms.",
      menus: "The links in your store's header and footer.",
      domains: "Connect your own web address to the store.",
      seo: "Search titles, descriptions and product feeds for Google and Facebook.",
      policies: "Refund, privacy, shipping and terms pages.",
      featured: "Best sellers, new arrivals, sale and hand-picked products on your home page.",
      branches: "Show your shops and outlets with address and hours.",
      sellingPoints: "Short promises like cash on delivery, shown on every product page.",
    },
  },

  comingSoon: {
    badge: "Coming soon",
    body: "This part of Adyvance is being built.",
    notLive: "Nothing here is live yet, and nothing you do elsewhere is affected.",
    goTo: "Go to {area}",
    backToday: "Back to Home",
    areas: {
      reviews: "Collect product reviews from customers, approve them and show them on your store.",
      loyalty: "Reward returning customers with points they can spend on their next order.",
      expenses:
        "Record shop expenses like rent, salaries and packaging, and see them in your profit.",
      suppliers: "Keep supplier details, what you owe them and every payment you make.",
      statements: "Download profit and loss and account statements for any period.",
      links: "Share a checkout link that takes a customer straight to payment for chosen items.",
      affiliates: "Let partners share your products and earn a commission on the sales they bring.",
      seo: "Set page titles, descriptions and product feeds so your store shows up in search.",
      paymentSettings: "Choose how customers can pay and connect payment providers in one place.",
      checkout: "Set checkout fields, minimum order values and guest checkout rules.",
      region: "Set your language, currency format, time zone and calendar.",
      importMarketplace: "Buy stock from suppliers outside Nepal through Adyvance.",
      digitalMarketing: "Help from the Adyvance team with ads, social media and campaigns.",
      designContent: "Photos, videos, banners and posts for your brand, made by the Adyvance team.",
      growthStrategy: "Advice from Adyvance on growing your sales.",
      smsService:
        "Send order updates, OTPs and offers to customers by SMS through Adyvance, from one sender name.",
      partialPayment:
        "Customers pay a deposit now and the rest on delivery, as an amount, a percentage or the delivery charge.",
      offerPopup: "Show a discount or announcement when customers open your store.",
      mcp: "Connect AI tools like Claude to your store through MCP, with your permissions and nothing more.",
      courses: "Courses from Adyvance on selling online.",
      conversations: "Customer messages from your store and social pages, in one place.",
      organization: "Manage your business details, owners and the stores in your organization.",
    },
    aiTitle: "Meet root, your AI assistant",
    aiAbout:
      "Ask root in plain words to write product descriptions, reply to customers and get things done.",
  },

  placeholder: {
    title: "{area}",
    body: "This area arrives in a later release. Nothing here is live yet.",
  },

  errors: {
    network: "Couldn't reach Adyvance. Check your connection and retry.",
    generic:
      "Something didn't work. Retry, and if it keeps failing, report a problem with the request ID.",
    forbidden: "You don't have permission for this. Ask the store owner.",
    notFound: "We couldn't find that. It may have been removed, or the link is wrong.",
    rateLimited: "Too many requests. Wait a moment and retry.",
    activationRequired: "This needs a service that isn't activated yet.",
    entitlement: "Your plan doesn't include this.",
    orgLocked: "This business is locked right now. Contact Adyvance support.",
    validation: "Some details need fixing.",
    stepUpCancelled: "Nothing was changed. Confirm it's you when asked, then try again.",
  },

  console: {
    signInTitle: "Mission control sign-in",
    signInLead: "Platform staff only. You will confirm with a second step after Google.",
    /** The sign-in stage: root at mission control (owner design, 2026-10-26). */
    mission: {
      badge: "Owner console",
      label: "Adyvance mission control",
      map: "Shops across Nepal",
      live: "Live",
      hello: "Namaste! Mission control is ready.",
      bye: "All set. Sign in to take the controls.",
      meet: "Mission control with root",
      toolOf: "Tool {n} of {total}",
      trust: {
        twoStep: "Google sign-in, then an authenticator code",
        recorded: "Every staff sign-in is recorded",
        totals: "Shop data shows as totals, never customers' details",
      },
      tools: {
        vendors: {
          title: "Vendors",
          p1: "Every shop and store in one list",
          p2: "Each shop's plan, usage and health",
          p3: "Help a shop when they ask",
        },
        money: {
          title: "Plans & Wallet",
          p1: "Plan prices, features and limits",
          p2: "Adyvance Wallet top-ups and credits",
          p3: "Promo codes and subscriptions",
        },
        import: {
          title: "Product Import",
          p1: "Answer shops' price requests",
          p2: "Track imports from abroad to Nepal",
          p3: "Keep shops updated at every step",
        },
        services: {
          title: "Services",
          p1: "Meta and Google ads for shops",
          p2: "SMS packs and sender names",
          p3: "Courses for sellers",
        },
        health: {
          title: "Platform health",
          p1: "Workers, jobs and queues at a glance",
          p2: "Capacity and releases",
          p3: "Feature flags and settings",
        },
        security: {
          title: "Security & support",
          p1: "Two-step sign-in for every staff member",
          p2: "Security events and reports",
          p3: "Support requests in one place",
        },
      },
    },
    mfaRequiredTitle: "Two-step sign-in required",
    mfaRequiredBody:
      "Platform accounts must have an authenticator app or recovery codes. Ask the platform owner to enrol you.",
    // D-081: first sign-in sets up the authenticator app here.
    enroll: {
      title: "Set up your authenticator app",
      lead: "Workspace asks for a code from your phone at every sign-in. Set it up once, now.",
      step1:
        "Scan this QR code with Google Authenticator, Microsoft Authenticator or a similar app.",
      qrLabel: "QR code for your authenticator app",
      openApp: "On this phone? Open in authenticator app",
      keyLabel: "Setup key",
      keyHelp:
        'On a computer? In the app choose "Enter a setup key", type this key and pick "Time based".',
      copyKey: "Copy key",
      keyCopied: "Setup key copied",
      codeLabel: "Enter the 6-digit code the app shows",
      confirm: "Turn on and continue",
      preparing: "Preparing your setup key…",
      alreadyEnrolled: "This account already has an authenticator. Sign in with its code.",
      openWorkspace: "I've saved them. Open workspace",
    },
    // D-081: My security (account menu).
    mySecurity: {
      title: "My security",
      appsTitle: "Authenticator apps",
      appsLead:
        "A code from any of these apps signs you in. Add a second phone so you're never locked out.",
      added: "Added {date}",
      lastUsed: "Last used {date}",
      notUsed: "Not used yet",
      remove: "Remove",
      removeConfirm: "Remove {name}? Its codes stop working at once.",
      keep: "Keep",
      removed: "Authenticator removed",
      lastOne: "This is your only authenticator. Add another one before removing it.",
      add: "Add authenticator",
      nameLabel: "Name",
      namePlaceholder: "e.g. Backup phone",
      start: "Continue",
      cancel: "Cancel",
      addedToast: "Authenticator added",
      recoveryTitle: "Recovery codes",
      recoveryLeft: "Unused codes left: {n}",
      recoveryLead: "Each code signs you in once if you lose your phones.",
      regenerate: "Get new codes",
      regenerateConfirm: "Get new codes? Your old codes stop working at once.",
      vendorNote: "The same authenticator also protects this account on the vendor admin.",
    },
    google: "Sign in with Google",
    googleFailed: "Google sign-in didn't finish. Try again with a platform staff account.",
    googleUnavailable: "Google sign-in isn't set up on this console yet.",
    googleNeedsServer:
      "Google sign-in needs the console server. It isn't available with mock data.",
    emailUnavailable: "Email sign-in isn't available yet. Use Google.",
    turnstileFailed:
      "We couldn't confirm this browser. Reload the page and try again, or use Google.",
    mfaLead: "Enter the code from your authenticator app, or use one of your recovery codes.",
    useRecovery: "Use a recovery code instead",
    useTotp: "Use your authenticator app instead",
    environment: "Environment: {env}",
    mfaOn: "Two-step sign-in on",
    privacyNote:
      "Cross-vendor views show totals and operational data only, never vendor customers' personal data.",
    nav: {
      overview: "Overview",
      vendors: "Vendors",
      analytics: "Analytics",
      health: "Health",
      capacity: "Capacity and cost",
      jobs: "Jobs",
      config: "Configuration",
      releases: "Releases",
      requests: "Feature requests",
      runner: "Dev runner",
      security: "Security and recovery",
      support: "Support",
      billing: "Wallet and billing",
      payments: "Payment methods",
      plans: "Plans & pricing",
    },
    sections: {
      overview:
        "Platform health, vendor counts, activity, incidents and jobs will show here once the platform APIs ship.",
      vendors: "The vendor directory and lifecycle pipeline (invited to closed) will list here.",
      analytics: "Sign-up funnel, active vendors and adoption, as totals only.",
      health: "Service status, error rates, latency and integration health.",
      capacity:
        "Cloudflare usage against Free-plan limits and the paid dependencies waiting for activation.",
      jobs: "Queues, dead-letter items and schedules, with audited retry and discard.",
      config: "Feature flags, plans and entitlements, providers and legal documents.",
      releases: "Versions per environment, staging promotion and rollback.",
      requests: "Feature requests mapped to the canonical registry.",
      runner: "The owner dev runner needs a paid build environment.",
      security:
        "Audit search, platform users and MFA, sessions, secret names and rotation dates, backups and incidents.",
      support: "Problem reports and tickets with attached context.",
      billing:
        "Shops' Adyvance Wallets: top-ups to check, balances, pack and theme prices, and the wallet settings.",
      payments:
        "How shops pay Adyvance: QR payment, bank transfer and Fonepay QR, set up once for every service.",
      plans: "The plans shops buy: price, icon, every feature, limits and monthly credits.",
    },
    runnerPending:
      "Waiting for activation: the dev runner needs a paid build environment (PAID_DEPENDENCIES).",
    // D-066: the console shell, built like the vendor admin's.
    navSections: {
      money: "Plans and money",
      services: "Adyvance Services",
      platform: "Platform",
      support: "Support and security",
    },
    shell: {
      title: "Owner console",
      sections: "Console sections",
      shops: "Shops",
      searchNote: "Type a shop's name or address to open it.",
      attention: "Needs you",
      attentionLabel: "Needs you: {count} waiting",
      attentionNone: "Nothing is waiting.",
      attentionAll: "Open the overview",
      env: { local: "Local", staging: "Staging", production: "Production" },
      role: "Role: {role}",
      mfaOn: "Two-step sign-in on",
    },
  },
  // Phase 5 modules (each owns src/en/<module>.ts).
  catalog: catalogStrings,
  storefront: storefrontStrings,
  commerce: commerceStrings,
  operations: operationsStrings,
  payments: paymentsStrings,
  fulfilment: fulfilmentStrings,
  integrations: integrationsStrings,
  imports: importsStrings,
  wallet: walletStrings,
  adyWallet: adyWalletStrings,
  plans: plansStrings,
  desk: deskStrings,
  billingOps: billingOpsStrings,
  platformOps: platformOpsStrings,
  trustOps: trustOpsStrings,
  notifications: notificationsStrings,
  analytics: analyticsStrings,
  engagement: engagementStrings,
  automation: automationStrings,
  marketing: marketingStrings,
  headless: headlessStrings,
  designs: designsStrings,
  settings: settingsStrings,
  importMarket: importMarketStrings,
  smsService: smsServiceStrings,
  digitalMarketing: digitalMarketingStrings,
  ads: adsStrings,
  payMethods: payMethodsStrings,
  courses: coursesStrings,
  conversations: conversationsStrings,
  daraz: darazStrings,
  aiMascot: aiMascotStrings,
} as const;

export type EnCatalogue = typeof en;
